import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import useAuth from "containers/auth/useHooks";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Notification {
  id: number;
  title: string;
  description: string;
  createdAt: string;
  isRead: boolean;
}

const NotificationDrawer: React.FC<DrawerProps> = ({ isOpen, onClose }) => {
  const { getNotifications, markNotificationAsRead, markAllNotificationAsRead} = useAuth();
  
  // State management
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  
  const LIMIT = 10; // Number of notifications to load per request

  const handleMarkAllAsRead =  () => {
 markAllNotificationAsRead();

  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  // Load initial notifications
  useEffect(() => {
    if (isOpen) {
      loadInitialNotifications();
    }
  }, [isOpen]);

  const loadInitialNotifications = async () => {
    setLoading(true);
    setOffset(0);
    setNotifications([]);
    
    try {
      const response = await getNotifications(
        (data: Notification[]) => {
          setNotifications(data);
          setTotalCount(data.length);
        },
        { offset: 0, limit: LIMIT }
      );
      
      if (response?.data) {
        setTotalCount(response.data.totalCount || response.data.notifications?.length || 0);
        setHasMore(response.data.notifications?.length === LIMIT);
      }
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreNotifications = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    const newOffset = offset + LIMIT;
    
    try {
      const response = await getNotifications(
        (data: Notification[]) => {
          setNotifications(prev => [...prev, ...data]);
        },
        { offset: newOffset, limit: LIMIT }
      );
      
      if (response?.data) {
        const newNotifications = response.data.notifications || [];
        setHasMore(newNotifications.length === LIMIT);
        setOffset(newOffset);
      }
    } catch (error) {
      console.error("Error loading more notifications:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleReadNotifications = async (id: number) => {
    const res = await markNotificationAsRead(id);
    if (res) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, isRead: true } : n
        )
      );
    }
  };

  // Group notifications by date
  const groupNotificationsByDate = (notifications: Notification[]) => {
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');
    
    const grouped = {
      today: [] as Notification[],
      yesterday: [] as Notification[],
      older: [] as Notification[]
    };

    notifications.forEach(notification => {
      const notificationDate = moment(notification.createdAt).startOf('day');
      
      if (notificationDate.isSame(today)) {
        grouped.today.push(notification);
      } else if (notificationDate.isSame(yesterday)) {
        grouped.yesterday.push(notification);
      } else {
        grouped.older.push(notification);
      }
    });

    return grouped;
  };

  const groupedNotifications = groupNotificationsByDate(notifications);

  // Notification Item Component
  const NotificationItem: React.FC<{
    notification: Notification;
    onMarkAsRead: (id: number) => void;
  }> = ({ notification, onMarkAsRead }) => (
    <div className="pb-4 mb-2 bordr-linear ">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center">
            <p className="text-blackie text-lg font-medium">
              {notification.title}
            </p>
            {/* {!notification.isRead && (
              <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 ml-2">
                new
              </span>
            )} */}
          </div>
          <p className="text-labelTextColor text-sm mt-1">
            {notification.description}
          </p>
          <p className="text-labelTextColor text-xs mt-2">
            {moment(notification.createdAt).fromNow()}
          </p>
        </div>
        {!notification.isRead && (
          <button
            className="bg-yellowBg text-yellowText text-xs rounded-full px-3 py-1 hover:bg-opacity-90 transition-colors duration-200 ml-2 flex-shrink-0"
            onClick={() => onMarkAsRead(notification.id)}
          >
            Mark as read
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-30 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      style={{ zIndex: 'calc(var(--z-drawer) + 1)' }}
      onClick={onClose}
    >
      <div
        className={`fixed top-0 right-0 h-full w-4/5 sm:w-1/2 md:w-1/3 lg:w-[465px] overflow-y-auto bg-white p-4 md:p-10 shadow-lg transition-transform duration-300 ease-in-out transform ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-2xl text-gray-600 hover:text-black"
        >
          &times;
        </button>
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-black">Notifications</h2>
          {
            notifications.filter(notification => !notification.isRead).length > 3 && (
              <button className="text-sm text-yellowText" onClick={handleMarkAllAsRead}>Mark all as read</button>
            )
          }
          
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellowText"></div>
          </div>
        )}

        {/* Notifications List */}
        {!loading && (
          <div className="space-y-6">
            {notifications.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-labelTextColor">No notifications to show.</p>
              </div>
            ) : (
              <>
                {/* Today Section */}
                {groupedNotifications.today.length > 0 && (
                  <div>
                    <h3 className="text-base font-semibold text-darkYellowText mb-4 ">Today</h3>
                    {groupedNotifications.today.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkAsRead={handleReadNotifications}
                      />
                    ))}
                  </div>
                )}

                {/* Yesterday Section */}
                {groupedNotifications.yesterday.length > 0 && (
                  <div>
                    <h3 className="text-base font-semibold text-darkYellowText mb-4">Yesterday</h3>
                    {groupedNotifications.yesterday.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkAsRead={handleReadNotifications}
                      />
                    ))}
                  </div>
                )}

                {/* Older Section */}
                {groupedNotifications.older.length > 0 && (
                  <div>
                    <h3 className="text-base font-semibold text-darkYellowText mb-4">Older</h3>
                    {groupedNotifications.older.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkAsRead={handleReadNotifications}
                      />
                    ))}
                  </div>
                )}

                {/* See More Button */}
                {hasMore && (
                  <div className="mt-6">
                    <button
                      onClick={loadMoreNotifications}
                      disabled={loadingMore}
                      className="w-full py-3 text-center text-sm font-medium text-yellowText bg-yellowBg rounded-full hover:bg-opacity-90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingMore ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellowText mr-2"></div>
                          Loading...
                        </div>
                      ) : (
                        `See more`
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationDrawer;
